/**
 * ecgKit — движок ЭКГ-визуализации краша (используется features/crash).
 *
 * heartbeat — форма одного удара (QRS-подобный пик).
 * createSampler — фабрика сэмплера: каждый кадр кладёт {t, v} в буфер
 *   (v — нормированное отклонение -1..1, амплитуду в px компонент
 *   считает сам под свою высоту), частота зависит от множителя.
 */

// Фазы раунда (значения совпадают с PHASES из features/crash/config/crashConfig.js)
const ECG_PHASES = {
  BETTING: 'betting',
  RUNNING: 'running',
  BUSTED: 'busted'
}

export const ECG_COLORS = {
  line: '#00FF7F',
  mint: '#4EFFC6',
  red: '#FF4B4B',
  info: '#4DDCFF',
  amber: '#FFB84D',
  dim: 'rgba(255,255,255,0.25)'
}

const DEFAULT_WINDOW_MS = 5000

// Форма одного удара (u = фаза 0..1): резкий R-пик + S-провал + T-волна
const heartbeat = (u) => {
  if (u < 0.08) return Math.sin((u / 0.08) * Math.PI)
  if (u < 0.16) return -0.35 * Math.sin(((u - 0.08) / 0.08) * Math.PI)
  if (u < 0.42) return 0.18 * Math.sin(((u - 0.16) / 0.26) * Math.PI)
  return 0
}

/**
 * Сэмплер ЭКГ. opts:
 * - windowMs — глубина буфера (мс)
 * - baseBps / bpsSlope — частота ударов: bps = base + logM * slope
 * - flatNoise — на прямой линии оставлять остаточный шум
 */
export const createSampler = () => {
  const st = { samples: [], beatPos: 0, lastTs: 0, roundId: 0 }
  return (r, now, opts = {}) => {
    if (st.roundId !== r.id) {
      st.roundId = r.id
      st.samples.length = 0
      st.beatPos = 0
    }
    const dt = Math.min(0.05, (now - (st.lastTs || now)) / 1000)
    st.lastTs = now

    const isBusted = r.phase === ECG_PHASES.BUSTED
    const isRunning = r.phase === ECG_PHASES.RUNNING
    const logM = Math.log(Math.max(100, r.multiplierHundreds) / 100)
    const bps = isBusted ? 0 : (opts.baseBps ?? 1.05) + logM * (opts.bpsSlope ?? 0.55)

    st.beatPos = (st.beatPos + dt * bps) % 1

    let v = 0
    if (isRunning) {
      v = heartbeat(st.beatPos)
    } else if (isBusted && opts.flatNoise) {
      v = (Math.random() - 0.5) * 0.04
    }

    st.samples.push({ t: now, v })
    const windowMs = opts.windowMs ?? DEFAULT_WINDOW_MS
    while (st.samples.length && now - st.samples[0].t > windowMs) st.samples.shift()

    return { samples: st.samples, beatPos: st.beatPos, bps, logM, isBusted, isRunning, windowMs }
  }
}

// Подгонка canvas под CSS-размер + DPR (вызывать каждый кадр — дёшево)
export const fitCanvas = (canvas) => {
  const dpr = window.devicePixelRatio || 1
  const w = canvas.clientWidth
  const h = canvas.clientHeight
  const W = Math.max(1, Math.round(w * dpr))
  const H = Math.max(1, Math.round(h * dpr))
  if (canvas.width !== W || canvas.height !== H) {
    canvas.width = W
    canvas.height = H
  }
  const ctx = canvas.getContext('2d')
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  return { ctx, w, h }
}

// Текущая амплитуда в px от logM и высоты сцены
export const ampOf = (logM, h) => Math.min(h * 0.3, 14 + logM * 13)
